import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SensorsIcon from '@mui/icons-material/Sensors';
import WaterOutlinedIcon from '@mui/icons-material/WaterOutlined';
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined';

import * as Constants from '../../components/api/constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

function createData(id, property, icon) {
  return { id, property, icon, log1: '...', log2: '...', log3: '...' };
}

function ConvertTime(value) {
  const num = Number(value) || 0;
  const hours = num >> 8;
  const minutes = num & 0xff;

  const hoursString = hours < 10 ? '0' + hours : String(hours);
  const minutesString = minutes < 10 ? '0' + minutes : String(minutes);

  return `${hoursString}:${minutesString}`;
}

function mapError(code) {
  switch (code) {
    case '1': return 'ER1 Sensor';
    case '2': return 'ER2 Sensor';
    case '3': return 'ER3 Prime';
    case '4': return 'ER4 Thermal trip';
    case '5': return 'ER5 Pool too Hot';
    case '6': return 'ER6 12V overload';
    case '7': return 'ER7 IPTS';
    case '8': return 'ER8 HVS';
    case '9': return 'ER9 EEPROM error';
    case '0':
    default:
      return '...';
  }
}

export default function HistoryFaultLogs() {
  const state = useContext(MainContext).state;

  const r9 = state.R9.split(',');
  const ra = state.RA.split(',');
  const rb = state.RB.split(',');

  const deviceId = state.selectedDevice;

  const ICON_COLORS = {
    heat: "#FF7043",        // orange / heat
    cool: "#4FC3F7",        // light blue
    water: "#29B6F6",       // pool / water
    time: "#BA68C8",        // timers
    power: "#FFD54F",       // electrical
    speed: "#81C784",       // motion / speed
    level: "#90A4AE",       // neutral / measurement
    error: "#FF0000"
  };

  // Base row config with icons
  const rows = [
    createData(0, 'Hour Meter', <HistoryIcon sx={{ color: ICON_COLORS.time }} />),
    createData(1, 'Time', <AccessTimeIcon sx={{ color: ICON_COLORS.time }} />),
    createData(2, 'Error Code', <ErrorOutlineIcon sx={{ color: ICON_COLORS.error }} />),
    createData(3, 'Mains current', <BoltIcon sx={{ color: ICON_COLORS.power }} />),
    createData(4, 'Mains voltage', <FlashOnIcon sx={{ color: ICON_COLORS.power }} />),
    createData(5, 'Pool temp', <WaterOutlinedIcon sx={{ color: ICON_COLORS.water }} />),
    createData(6, 'Heater temp', <LocalFireDepartmentIcon sx={{ color: ICON_COLORS.heat }} />),
    createData(7, 'Case temp', <DeviceThermostatIcon sx={{ color: ICON_COLORS.cool }} />),
    createData(8, 'Pool in use', <SensorsIcon sx={{ color: ICON_COLORS.level }} />),
    createData(9, 'Element power', <WhatshotOutlinedIcon sx={{ color: ICON_COLORS.heat }} />),
    createData(10, 'Set temp', <DeviceThermostatIcon sx={{ color: ICON_COLORS.cool }} />),
  ];

  // Helper to format one log column set (rX)
  const applyLogColumn = (rowsArr, data, key) => {
    if (!data || data.length < 13) return;

    // Hr Meter
    rowsArr[0][key] = (Number(data[2]) / 10.0).toFixed(1);

    // Time
    rowsArr[1][key] = ConvertTime(data[3]);

    // Error code mapped
    rowsArr[2][key] = mapError(data[4]);

    // Mains current
    rowsArr[3][key] = `${(Number(data[5]) / 10.0).toFixed(1)}A`;

    // Mains voltage
    rowsArr[4][key] = `${data[6]}V`;

    // Pool temp
    rowsArr[5][key] = `${(Number(data[7]) / 10.0).toFixed(1)}°C`;

    // Heater temp
    rowsArr[6][key] = `${(Number(data[8]) / 10.0).toFixed(1)}°C`;

    // Case temp
    rowsArr[7][key] = `${data[9]}°C`;

    // Pool in use
    rowsArr[8][key] = data[10] === '1' ? 'Yes' : 'No';

    // Element power
    if (data[11] === '255') {
      rowsArr[9][key] = 'Off';
    } else {
      rowsArr[9][key] = `${data[11]}°C`;
    }

    // Set temp
    rowsArr[10][key] = `${(Number(data[12]) / 10.0).toFixed(1)}°C`;
  };

  // Fill log1/log2/log3 from r9/ra/rb
  applyLogColumn(rows, r9, 'log1');
  applyLogColumn(rows, ra, 'log2');
  applyLogColumn(rows, rb, 'log3');

  const sendCommand = (command, value) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        DeviceId: deviceId,
        Command: command,
        Value: value,
      }),
      credentials: 'include',
    };

    fetch(Constants.baseURL + Constants.apiSendCommand, requestOptions)
      .then((response) => {
        if (response.ok) {
          toast.success('Command sent successfully. Refreshing...', {
            position: 'top-right',
            autoClose: 10000,
          });

          setTimeout(() => state.refreshDeviceRF(deviceId), 6000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 12000);
        } else {
          toast.error('Send failed. Please try again', {
            position: 'top-right',
            autoClose: 5000,
          });
        }
      })
      .catch((error) => console.log('request failed', error));
  };

  const onClickListener = () => {
    sendCommand('clearFaultLogs', '');
  };

  return (
    <>
      <ToastContainer />

      <Typography
        component="h2"
        variant="h6"
        sx={{ color: 'white', fontWeight: 600, mb: 2 }}
      >
        Fault Logs
      </Typography>

      <Grid container spacing={1.5}>
        {rows.map((row) => (
          <Grid item xs={12} md={6} key={row.id}>
            <Paper
              sx={{
                p: 1.8,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                transition: '0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Header row: icon + title */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',   // 👈 vertical centering
                  gap: 1.5,
                }}
              >
                {/* Icon bubble */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    flexShrink: 0,
                  }}
                >
                  {row.icon}
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {row.property}
                </Typography>
              </Box>

              <Box sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[1, 2, 3].map((n) => (
                        <TableCell
                          key={n}
                          align="center"
                          sx={{
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: 11,
                            fontWeight: 700,
                            borderBottom: '1px solid rgba(255,255,255,0.15)',
                            py: 0.5,
                          }}
                        >
                          {n}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      {[row.log1, row.log2, row.log3].map((val, idx) => (
                        <TableCell
                          key={idx}
                          align="center"
                          sx={{
                            color: 'white',
                            fontSize: 13,
                            fontWeight: 600,
                            borderBottom: 'none',
                            py: 0.8,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {val}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Clear button tile */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              mt: 0.5,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              textAlign: 'center',
            }}
          >
            <Button
              variant="outlined"
              onClick={onClickListener}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                px: 4,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              Clear Fault Logs
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
